import * as React from "react";
import {useMemo, useState} from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField
} from "@mui/material";
import {DateTimePicker} from "@mui/x-date-pickers";
import dayjs, {Dayjs} from "dayjs";

export type TaskDialogMode = "create" | "edit";

export interface TaskDialogOption {
  id: string;
  label: string;
}

export interface TaskDialogValues {
  id?: string;
  subject: string;
  hiddenPreview: string;
  scheduleDate: Dayjs | null;
  destinationListId: string;
  templateId: string;
}

export interface TaskDialogProps {
  open: boolean;
  mode?: TaskDialogMode; // default: "create"
  initialValues?: Partial<TaskDialogValues>;
  onClose: () => void;
  onSubmit: (values: TaskDialogValues) => Promise<void> | void;

  // Data for selects
  destinationOptions: TaskDialogOption[]; // {id,label}
  templateOptions: TaskDialogOption[];     // {id,label}

  // UI control
  submitting?: boolean; // controlled by parent (e.g., RTKQ isLoading)
}

const DEFAULT_VALUES: TaskDialogValues = {
  subject: "",
  hiddenPreview: "",
  scheduleDate: dayjs(),
  destinationListId: "",
  templateId: ""
};

export default function TaskDialog({
                                     open,
                                     mode = "create",
                                     initialValues,
                                     onClose,
                                     onSubmit,
                                     destinationOptions,
                                     templateOptions,
                                     submitting = false
                                   }: TaskDialogProps) {
  const [values, setValues] = useState<TaskDialogValues>({
    ...DEFAULT_VALUES,
    ...initialValues,
  });

  const [touched, setTouched] = useState<Record<keyof TaskDialogValues, boolean>>({
    id: true,
    subject: false,
    hiddenPreview: false,
    scheduleDate: false,
    destinationListId: false,
    templateId: false
  });

  const errors = useMemo(() => {
    const nowMinus5 = dayjs().subtract(5, "minute");
    return {
      subject: values.subject.trim() ? "" : "Subject is required",
      hiddenPreview: values.hiddenPreview.trim() ? "" : "Hidden preview is required",
      scheduleDate:
        values.scheduleDate && values.scheduleDate.isAfter(nowMinus5)
          ? ""
          : "Schedule date must be now or later",
      destinationListId: values.destinationListId ? "" : "Select a destination email list",
      templateId: values.templateId ? "" : "Choose a design template"
    } as Record<keyof TaskDialogValues, string>;
  }, [values]);

  const isValid = useMemo(() => Object.values(errors).every((e) => !e), [errors]);

  function handleChange<K extends keyof TaskDialogValues>(key: K, val: TaskDialogValues[K]) {
    setValues((prev) => ({...prev, [key]: val}));
  }

  function handleBlur<K extends keyof TaskDialogValues>(key: K) {
    setTouched((prev) => ({...prev, [key]: true}));
  }

  const touchAll = () =>
    setTouched({
      id: true,
      subject: true,
      hiddenPreview: true,
      scheduleDate: true,
      destinationListId: true,
      templateId: true
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    touchAll();
    if (!isValid || !values.scheduleDate) return;

    await onSubmit({
      subject: values.subject.trim(),
      hiddenPreview: values.hiddenPreview.trim(),
      scheduleDate: values.scheduleDate,
      destinationListId: values.destinationListId,
      templateId: values.templateId
    });
  };

  // If initialValues change while dialog open (e.g., edit mode), you can re-sync:
  React.useEffect(() => {
    if (!open) return;
    setValues({
      ...DEFAULT_VALUES,
      ...initialValues,
    });
    setTouched({
      id: true,
      subject: false,
      hiddenPreview: false,
      scheduleDate: false,
      destinationListId: false,
      templateId: false
    });
  }, [open, initialValues]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === "edit" ? "Update Email Task" : "Schedule Email Task"}
      </DialogTitle>

      <form onSubmit={handleSubmit} noValidate>
        <DialogContent>
          <Stack spacing={2}>
            <TextField
              required
              label="Subject"
              value={values.subject}
              onChange={(e) => handleChange("subject", e.target.value)}
              onBlur={() => handleBlur("subject")}
              error={touched.subject && Boolean(errors.subject)}
              helperText={touched.subject && errors.subject}
              fullWidth
            />

            <TextField
              required
              label="Hidden Preview"
              placeholder="This appears in email preview text"
              value={values.hiddenPreview}
              onChange={(e) => handleChange("hiddenPreview", e.target.value)}
              onBlur={() => handleBlur("hiddenPreview")}
              error={touched.hiddenPreview && Boolean(errors.hiddenPreview)}
              helperText={touched.hiddenPreview && errors.hiddenPreview}
              fullWidth
              multiline
              minRows={2}
            />

            <DateTimePicker
              label="Schedule Date"
              value={values.scheduleDate}
              onChange={(newVal) => handleChange("scheduleDate", newVal)}
              onError={() => void 0}
              disablePast
              minutesStep={5}
              slotProps={{
                textField: {
                  required: true,
                  onBlur: () => handleBlur("scheduleDate"),
                  error: touched.scheduleDate && Boolean(errors.scheduleDate),
                  helperText: touched.scheduleDate && errors.scheduleDate,
                  fullWidth: true
                }
              }}
            />

            {/* Destination Email List */}
            <FormControl
              fullWidth required
              error={touched.destinationListId && Boolean(errors.destinationListId)}
            >
              <InputLabel id="destination-email-list-label">Destination Email List</InputLabel>
              <Select
                labelId="destination-email-list-label"
                label="Destination Email List"
                value={values.destinationListId}
                onChange={(e) => handleChange("destinationListId", e.target.value as string)}
                onBlur={() => handleBlur("destinationListId")}
              >
                {destinationOptions && destinationOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id} >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {touched.destinationListId && errors.destinationListId}
              </FormHelperText>
            </FormControl>

            {/* Design Template List */}
            <FormControl
              fullWidth required
              error={touched.templateId && Boolean(errors.templateId)}
            >
              <InputLabel id="design-template-list-label">Design Template</InputLabel>
              <Select
                labelId="design-template-list-label"
                label="Design Template"
                value={values.templateId}
                onChange={(e) => handleChange("templateId", e.target.value as string)}
                onBlur={() => handleBlur("templateId")}
              >
                {templateOptions && templateOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {touched.templateId && errors.templateId}
              </FormHelperText>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!isValid || submitting}>
            {submitting ? (mode === "edit" ? "Updating..." : "Scheduling...") : (mode === "edit" ? "Update" : "Schedule")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
